/**
 * Service for validating and returning appropriate error messages for form
 * validation rules
 */
import rules from '../validator';

/**
 * Returns an appropriate error message for email address validation
 *
 * @param {string} value - Email address to test
 * @returns {string} - Error message if email is invalid or undefined otherwise
 */
export const email = (value) => (rules.isEmail(value) ? undefined : 'Invalid email address');

/**
 * Returns an appropriate error message for max length validation
 *
 * @param {number} length - Maximum number of chars the value should have
 * @param {string} value - String to test
 * @returns {function} - Function that will return a string error message if
 *   the value does not meet the maximum length or undefined otherwise
 */
export const maxLength = (length) => (value) =>
  rules.hasMaxLength(length)(value) ? undefined : `Field must be ${length} characters or less`;

/**
 * Returns an appropriate error message for min length validation
 *
 * @param {number} length - Minimum number of chars the value should have
 * @param {string} value - String to test
 * @returns {function} - Function that will return a string error message if
 *   the value does not meet the minimum length or undefined otherwise
 */
export const minLength = (length) => (value) =>
  rules.hasMinLength(length)(value) ? undefined : `Field must be at least ${length} characters`;

/**
 * Returns an appropriate error message for required field validation
 *
 * @param {string} value - String to test
 * @returns {string} - Error message if the value is not defined or undefined
 *   otherwise
 */
export const required = (value) => (rules.isRequired(value) ? undefined : 'Field required');

/**
 * Returns an appropriate error message for the required field validation
 *
 * @param {string} value - String to test
 * @returns {string} - Error message if the value contains something other than
 *  alphanumeric characters or underscores
 */
export const isAlphanumericUnderscore = (value) =>
  rules.isAlphanumericUnderscore(value)
    ? undefined
    : 'Field must be alphanumeric and can contain underscores';

/**
 * Returns an appropriate error message for the required field validation
 *
 * @param {string} value - String to test
 * @returns {string} - Error message if the value is something other than a
 *   valid IPv4 address or range
 */
export const validateCidr = (value) =>
  rules.isCidr(value) ? undefined : 'Must be a valid IPv4 address or range';

export const aclItemUnique = (value, values, { newAcl, newInstanceAcls }) =>
  rules.isAclUnique(newAcl, newInstanceAcls) ? undefined : 'Entry already exists in ACL';

export const isUserUniqueValidation = (value, values, users, invites) =>
  rules.isUserUnique(value, users, invites) ? undefined : 'User already exists';

export default {
  email,
  maxLength,
  minLength,
  required,
  isAlphanumericUnderscore,
};
